import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { servicesData } from '../../mocks/services';
import { reviewsData } from '../../mocks/reviews';
import { videosData } from '../../mocks/videos';

export default function HomePage() {
  const [activeService, setActiveService] = useState('roofing');
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState<any>(null);
  const [submittedReviews, setSubmittedReviews] = useState<any[]>([]);
  const [allReviews, setAllReviews] = useState(reviewsData);

  // Fetch submitted reviews from form storage
  useEffect(() => {
    const fetchSubmittedReviews = async () => {
      try {
        // Using the correct form ID that matches the review form action
        const response = await fetch('https://readdy.ai/api/form/d672ukr8scpqk7lbjm0g/submissions');
        if (response.ok) {
          const data = await response.json();
          if (data && Array.isArray(data)) {
            const formattedReviews = data.map((submission: any, index: number) => ({
              id: `submitted-${index}`,
              name: submission.reviewer_name || 'Anonymous',
              rating: 5,
              date: new Date(submission.submitted_at || Date.now()).toLocaleDateString('en-US'),
              service: submission.service_type || 'General',
              text: submission.review_text || '',
              avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(submission.reviewer_name || 'Anonymous')}&background=00D9FF&color=fff`
            }));
            setSubmittedReviews(formattedReviews);
            // Combine submitted reviews with mock reviews - submitted reviews first
            setAllReviews([...formattedReviews, ...reviewsData]);
          }
        } else if (response.status === 404) {
          // No reviews submitted yet - this is normal, just use mock reviews
          setAllReviews(reviewsData);
        }
      } catch (error) {
        // Network error or other issue - just use mock reviews
        setAllReviews(reviewsData);
      }
    };

    fetchSubmittedReviews();
  }, []);

  // Handle anchor scrolling on page load
  useEffect(() => {
    const hash = window.location.hash;
    if (hash === '#reviews') {
      setTimeout(() => {
        const element = document.getElementById('reviews');
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  }, []);

  const handleVideoClick = (video: any) => {
    setSelectedVideo(video);
    setShowVideoModal(true);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/541d87eba37d9b55e949983656dbba4c.jpg"
            alt="Spyder Restoration"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/60"></div>
          
          {/* Lightning Effects - Small bolts above roof area */}
          <div className="absolute inset-0 pointer-events-none">
            {/* Lightning 1 - Left side */}
            <svg className="absolute top-[15%] left-[25%] w-10 h-28 opacity-0 animate-lightning-1 lightning-bolt" viewBox="0 0 30 90" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <linearGradient id="lightning-gradient-1" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" style={{ stopColor: '#60a5fa', stopOpacity: 1 }} />
                  <stop offset="50%" style={{ stopColor: '#ffffff', stopOpacity: 1 }} />
                  <stop offset="100%" style={{ stopColor: '#3b82f6', stopOpacity: 0.3 }} />
                </linearGradient>
                <filter id="glow-1">
                  <feGaussianBlur stdDeviation="1.5" result="coloredBlur"/>
                  <feMerge>
                    <feMergeNode in="coloredBlur"/>
                    <feMergeNode in="SourceGraphic"/>
                  </feMerge>
                </filter>
              </defs>
              <path d="M 15 0 L 13 20 L 16 20 L 12 45 L 15 45 L 10 70 L 13 70 L 8 90" 
                    stroke="url(#lightning-gradient-1)" strokeWidth="1.2" fill="none" strokeLinecap="round" strokeLinejoin="round" filter="url(#glow-1)"/>
            </svg>

            {/* Lightning 2 - Center left */}
            <svg className="absolute top-[12%] left-[35%] w-9 h-24 opacity-0 animate-lightning-2 lightning-bolt" viewBox="0 0 28 80" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <linearGradient id="lightning-gradient-2" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" style={{ stopColor: '#60a5fa', stopOpacity: 1 }} />
                  <stop offset="50%" style={{ stopColor: '#ffffff', stopOpacity: 1 }} />
                  <stop offset="100%" style={{ stopColor: '#3b82f6', stopOpacity: 0.3 }} />
                </linearGradient>
                <filter id="glow-2">
                  <feGaussianBlur stdDeviation="1.5" result="coloredBlur"/>
                  <feMerge>
                    <feMergeNode in="coloredBlur"/>
                    <feMergeNode in="SourceGraphic"/>
                  </feMerge>
                </filter>
              </defs>
              <path d="M 14 0 L 12 18 L 15 18 L 11 40 L 14 40 L 9 65 L 12 65 L 7 80" 
                    stroke="url(#lightning-gradient-2)" strokeWidth="1.1" fill="none" strokeLinecap="round" strokeLinejoin="round" filter="url(#glow-2)"/>
            </svg>

            {/* Lightning 3 - Center */}
            <svg className="absolute top-[10%] left-[48%] w-11 h-32 opacity-0 animate-lightning-3 lightning-bolt" viewBox="0 0 32 100" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <linearGradient id="lightning-gradient-3" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" style={{ stopColor: '#60a5fa', stopOpacity: 1 }} />
                  <stop offset="50%" style={{ stopColor: '#ffffff', stopOpacity: 1 }} />
                  <stop offset="100%" style={{ stopColor: '#3b82f6', stopOpacity: 0.3 }} />
                </linearGradient>
                <filter id="glow-3">
                  <feGaussianBlur stdDeviation="1.5" result="coloredBlur"/>
                  <feMerge>
                    <feMergeNode in="coloredBlur"/>
                    <feMergeNode in="SourceGraphic"/>
                  </feMerge>
                </filter>
              </defs>
              <path d="M 16 0 L 14 22 L 17 22 L 13 40 L 16 40 L 11 65 L 14 65 L 9 80" 
                    stroke="url(#lightning-gradient-3)" strokeWidth="1.3" fill="none" strokeLinecap="round" strokeLinejoin="round" filter="url(#glow-3)"/>
            </svg>

            {/* Lightning 4 - Center right */}
            <svg className="absolute top-[13%] right-[35%] w-9 h-26 opacity-0 animate-lightning-4 lightning-bolt" viewBox="0 0 28 85" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <linearGradient id="lightning-gradient-4" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" style={{ stopColor: '#60a5fa', stopOpacity: 1 }} />
                  <stop offset="50%" style={{ stopColor: '#ffffff', stopOpacity: 1 }} />
                  <stop offset="100%" style={{ stopColor: '#3b82f6', stopOpacity: 0.3 }} />
                </linearGradient>
                <filter id="glow-4">
                  <feGaussianBlur stdDeviation="1.5" result="coloredBlur"/>
                  <feMerge>
                    <feMergeNode in="coloredBlur"/>
                    <feMergeNode in="SourceGraphic"/>
                  </feMerge>
                </filter>
              </defs>
              <path d="M 14 0 L 12 19 L 15 19 L 11 42 L 14 42 L 9 68 L 12 68 L 7 85" 
                    stroke="url(#lightning-gradient-4)" strokeWidth="1.1" fill="none" strokeLinecap="round" strokeLinejoin="round" filter="url(#glow-4)"/>
            </svg>

            {/* Lightning 5 - Right side */}
            <svg className="absolute top-[16%] right-[25%] w-10 h-30 opacity-0 animate-lightning-5 lightning-bolt" viewBox="0 0 30 95" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <linearGradient id="lightning-gradient-5" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" style={{ stopColor: '#60a5fa', stopOpacity: 1 }} />
                  <stop offset="50%" style={{ stopColor: '#ffffff', stopOpacity: 1 }} />
                  <stop offset="100%" style={{ stopColor: '#3b82f6', stopOpacity: 0.3 }} />
                </linearGradient>
                <filter id="glow-5">
                  <feGaussianBlur stdDeviation="1.5" result="coloredBlur"/>
                  <feMerge>
                    <feMergeNode in="coloredBlur"/>
                    <feMergeNode in="SourceGraphic"/>
                  </feMerge>
                </filter>
              </defs>
              <path d="M 15 0 L 13 21 L 16 21 L 12 47 L 15 47 L 10 72 L 13 72 L 8 95" 
                    stroke="url(#lightning-gradient-5)" strokeWidth="1.2" fill="none" strokeLinecap="round" strokeLinejoin="round" filter="url(#glow-5)"/>
            </svg>
            
            {/* Subtle flash overlay for lightning effect */}
            <div className="absolute inset-0 bg-blue-100 opacity-0 animate-flash"></div>
          </div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="mb-8">
            <img
              src="https://static.readdy.ai/image/2733261fde4eb6ca5d5b92336d650a8a/e8d9864e0d1c345c04aa52accd571aeb.png"
              alt="Spyder Restoration Logo"
              className="h-32 sm:h-40 lg:h-48 mx-auto drop-shadow-2xl"
            />
          </div>
          
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6 drop-shadow-lg">
            MICHIANA'S PREMIER<br />RESTORATION EXPERTS
          </h1>
          
          <p className="text-xl sm:text-2xl md:text-3xl text-cyan-400 mb-8 font-semibold" style={{ fontFamily: 'Orbitron, sans-serif' }}>
            Roofing • Siding • Gutters • Storm Damage
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            <a
              href="#quote"
              className="px-8 py-4 bg-gradient-to-r from-cyan-500 to-cyan-600 text-white text-lg font-bold rounded-full hover:from-cyan-600 hover:to-cyan-700 transition-all duration-300 shadow-lg hover:shadow-cyan-500/50 whitespace-nowrap cursor-pointer"
            >
              GET FREE QUOTE <i className="ri-arrow-right-line ml-2"></i>
            </a>
            <a
              href="tel:5743503050"
              className="px-8 py-4 bg-white/10 backdrop-blur-sm text-white text-lg font-bold rounded-full border-2 border-white hover:bg-white hover:text-gray-900 transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              EMERGENCY SERVICE 24/7
            </a>
          </div>

          <div className="flex flex-wrap justify-center gap-6 text-white">
            <a href="tel:5743503050" className="flex items-center gap-2 hover:text-cyan-400 transition-colors cursor-pointer">
              <i className="ri-phone-fill text-cyan-400 text-xl"></i>
              <span className="text-sm sm:text-base">574-350-3050</span>
            </a>
            <a href="https://wa.me/12693074842" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:text-cyan-400 transition-colors cursor-pointer">
              <i className="ri-whatsapp-fill text-cyan-400 text-xl"></i>
              <span className="text-sm sm:text-base">269-307-4842</span>
            </a>
            <a href="mailto:spyderrestorationllcmi@gmail.com" className="flex items-center gap-2 hover:text-cyan-400 transition-colors cursor-pointer">
              <i className="ri-mail-fill text-cyan-400 text-xl"></i>
              <span className="text-sm sm:text-base">spyderrestorationllcmi@gmail.com</span>
            </a>
          </div>
        </div>

        {/* Insurance Claims Badge */}
        <div className="absolute bottom-8 right-8 hidden lg:block">
          <div className="bg-white/95 backdrop-blur-sm p-6 rounded-2xl shadow-2xl border-2 border-cyan-400 max-w-sm animate-pulse">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-12 h-12 bg-cyan-500 rounded-full flex items-center justify-center">
                <i className="ri-shield-check-fill text-white text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900">WE HANDLE YOUR<br />INSURANCE CLAIMS</h3>
            </div>
            <p className="text-2xl font-bold text-cyan-600">YOU PAY $0</p>
          </div>
        </div>
      </section>

      {/* Trust Indicators Strip */}
      <section className="bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center items-center gap-8 md:gap-12">
            <div className="text-center">
              <p className="text-3xl font-bold text-cyan-400">10+</p>
              <p className="text-sm text-gray-300">Years Experience</p>
            </div>
            <div className="h-12 w-px bg-cyan-400/30 hidden md:block"></div>
            <div className="text-center">
              <p className="text-3xl font-bold text-cyan-400">1000+</p>
              <p className="text-sm text-gray-300">Projects Completed</p>
            </div>
            <div className="h-12 w-px bg-cyan-400/30 hidden md:block"></div>
            <div className="text-center">
              <p className="text-3xl font-bold text-cyan-400">A+</p>
              <p className="text-sm text-gray-300">BBB Rating</p>
            </div>
            <div className="h-12 w-px bg-cyan-400/30 hidden md:block"></div>
            <div className="text-center">
              <p className="text-3xl font-bold text-cyan-400">100%</p>
              <p className="text-sm text-gray-300">Satisfaction</p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-cyan-500 font-semibold mb-2 text-sm uppercase tracking-wider">OUR EXPERTISE</p>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              COMPREHENSIVE RESTORATION SERVICES
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              From roofing to siding, we provide complete restoration solutions with premium materials and expert craftsmanship
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {servicesData.map((service) => (
              <Link
                key={service.id}
                to={`/services/${service.slug}`}
                className="group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 cursor-pointer"
              >
                <div className="relative h-56 overflow-hidden">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                  <div className="absolute top-4 right-4 w-12 h-12 bg-cyan-500 rounded-full flex items-center justify-center shadow-lg">
                    <i className={`${service.icon} text-white text-2xl`}></i>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">{service.title}</h3>
                  <p className="text-gray-600 mb-4">{service.description}</p>
                  <ul className="space-y-2 mb-6">
                    {service.features.slice(0, 3).map((feature, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-sm text-gray-700">
                        <i className="ri-check-line text-cyan-500"></i>
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <div className="flex items-center text-cyan-600 font-semibold group-hover:text-cyan-700">
                    VIEW GALLERY &amp; DETAILS <i className="ri-arrow-right-line ml-2 group-hover:translate-x-2 transition-transform"></i>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Insurance Claims Section */}
      <section className="py-20 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                INSURANCE CLAIMS?<br />WE'VE GOT YOU COVERED
              </h2>
              <div className="w-20 h-1 bg-cyan-400 mb-8"></div>
              
              <div className="space-y-6 mb-8">
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-cyan-500 rounded-full flex items-center justify-center text-white font-bold text-xl">1</div>
                  <div>
                    <h3 className="text-xl font-bold text-white mb-2">FREE INSPECTION</h3>
                    <p className="text-gray-300">We assess the damage and document everything for your claim</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-cyan-500 rounded-full flex items-center justify-center text-white font-bold text-xl">2</div>
                  <div>
                    <h3 className="text-xl font-bold text-white mb-2">WE HANDLE THE PAPERWORK</h3>
                    <p className="text-gray-300">Our team works directly with your insurance company</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-cyan-500 rounded-full flex items-center justify-center text-white font-bold text-xl">3</div>
                  <div>
                    <h3 className="text-xl font-bold text-white mb-2">YOU PAY NOTHING</h3>
                    <p className="text-gray-300">Insurance covers the cost - zero out of pocket for you</p>
                  </div>
                </div>
              </div>

              <a
                href="#quote"
                className="inline-block px-8 py-4 bg-cyan-500 text-white text-lg font-bold rounded-full hover:bg-cyan-600 transition-all duration-300 shadow-lg hover:shadow-cyan-500/50 whitespace-nowrap cursor-pointer"
              >
                START YOUR CLAIM <i className="ri-arrow-right-line ml-2"></i>
              </a>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-4">
                <img
                  src="https://readdy.ai/api/search-image?query=professional%20roofing%20contractor%20inspecting%20storm%20damaged%20roof%20with%20clipboard%20documenting%20damage%20for%20insurance%20claim%20construction%20photography&width=400&height=500&seq=claim1&orientation=portrait"
                  alt="Insurance Inspection"
                  className="w-full h-64 object-cover object-top rounded-2xl shadow-lg border-2 border-cyan-400"
                />
                <img
                  src="https://readdy.ai/api/search-image?query=completed%20roof%20restoration%20project%20beautiful%20home%20after%20insurance%20claim%20repair%20professional%20construction%20work%20finished&width=400&height=300&seq=claim2&orientation=landscape"
                  alt="Completed Work"
                  className="w-full h-48 object-cover object-top rounded-2xl shadow-lg"
                />
              </div>
              <div className="space-y-4 pt-8">
                <img
                  src="https://readdy.ai/api/search-image?query=insurance%20adjuster%20and%20contractor%20meeting%20homeowner%20discussing%20claim%20approval%20construction%20consultation&width=400&height=300&seq=claim3&orientation=landscape"
                  alt="Insurance Meeting"
                  className="w-full h-48 object-cover object-top rounded-2xl shadow-lg"
                />
                <img
                  src="https://readdy.ai/api/search-image?query=before%20and%20after%20storm%20damage%20restoration%20home%20transformation%20insurance%20covered%20repair%20construction%20photography&width=400&height=500&seq=claim4&orientation=portrait"
                  alt="Before After"
                  className="w-full h-64 object-cover object-top rounded-2xl shadow-lg border-2 border-cyan-400"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quote Form Section */}
      <section id="quote" className="py-20 bg-gradient-to-br from-gray-50 to-cyan-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-12">
            <div className="text-center mb-8">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
                GET YOUR FREE ESTIMATE
              </h2>
              <div className="w-20 h-1 bg-cyan-500 mx-auto"></div>
            </div>

            <form
              id="quote-form"
              data-readdy-form
              action="https://readdy.ai/api/form/d5prpt0tu5df1co4d2dg"
              method="POST"
              className="space-y-6"
              onSubmit={(e) => {
                e.preventDefault();
                const form = e.currentTarget;
                const formData = new FormData(form);
                
                fetch(form.action, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                  },
                  body: new URLSearchParams(formData as any).toString(),
                })
                  .then(() => {
                    alert('Thank you! We will contact you shortly.');
                    form.reset();
                  })
                  .catch(() => {
                    alert('Something went wrong. Please try again or call us directly.');
                  });
              }}
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Full Name *</label>
                  <input
                    type="text"
                    name="name"
                    required
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-cyan-500 focus:outline-none transition-colors"
                    placeholder="John Doe"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Email *</label>
                  <input
                    type="email"
                    name="email"
                    required
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-cyan-500 focus:outline-none transition-colors"
                    placeholder="john@example.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Phone *</label>
                  <input
                    type="tel"
                    name="phone"
                    required
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-cyan-500 focus:outline-none transition-colors"
                    placeholder="574-350-3050"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Address *</label>
                  <input
                    type="text"
                    name="address"
                    required
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-cyan-500 focus:outline-none transition-colors"
                    placeholder="123 Main St, Elkhart, IN"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Services Needed *</label>
                <select
                  name="service"
                  required
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-cyan-500 focus:outline-none transition-colors"
                >
                  <option value="">Select a service</option>
                  <option value="Roofing">Roofing</option>
                  <option value="Siding">Siding</option>
                  <option value="Gutters">Gutters</option>
                  <option value="Windows">Windows</option>
                  <option value="Doors">Doors</option>
                  <option value="Storm Damage">Storm Damage</option>
                  <option value="Multiple Services">Multiple Services</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Project Description</label>
                <textarea
                  name="description"
                  rows={4}
                  maxLength={500}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-cyan-500 focus:outline-none transition-colors resize-none"
                  placeholder="Tell us about your project... (max 500 characters)"
                ></textarea>
              </div>

              <div className="bg-amber-50 border-2 border-amber-400 rounded-lg p-4">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    name="financing"
                    value="Yes"
                    className="w-5 h-5 text-amber-600 border-gray-300 rounded focus:ring-amber-500"
                  />
                  <span className="text-sm font-semibold text-gray-900">
                    <i className="ri-money-dollar-circle-line text-amber-600 mr-1"></i>
                    I'm interested in financing options
                  </span>
                </label>
              </div>

              <button
                type="submit"
                className="w-full py-4 bg-gradient-to-r from-cyan-500 to-cyan-600 text-white text-lg font-bold rounded-full hover:from-cyan-600 hover:to-cyan-700 transition-all duration-300 shadow-lg hover:shadow-cyan-500/50 whitespace-nowrap cursor-pointer"
              >
                SUBMIT REQUEST <i className="ri-arrow-right-line ml-2"></i>
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* Material Partners */}
      <section className="py-16 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-white text-center mb-12">
            TRUSTED MATERIAL PARTNERS
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {['GAF', 'CertainTeed', 'BBB A+', 'James Hardie'].map((partner) => (
              <div key={partner} className="bg-white rounded-xl p-6 flex items-center justify-center h-32 hover:shadow-lg hover:shadow-cyan-500/30 transition-all duration-300 group">
                <span className="text-2xl font-bold text-gray-800 group-hover:text-cyan-600 transition-colors">{partner}</span>
              </div>
            ))}
          </div>
          <p className="text-center text-gray-400 mt-8">Premium materials, lifetime warranties</p>
        </div>
      </section>

      {/* Certifications & Training Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-cyan-500 font-semibold mb-2 text-sm uppercase tracking-wider">PROFESSIONAL DEVELOPMENT</p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              CERTIFIED EXPERTS AT YOUR SERVICE
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              We continuously invest in training and certifications to deliver the highest quality workmanship and stay ahead of industry standards
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Certificate Display */}
            <div className="relative group">
              <div className="absolute -inset-4 bg-gradient-to-r from-amber-400 via-yellow-500 to-amber-400 rounded-2xl opacity-75 blur-lg group-hover:opacity-100 transition-opacity duration-500"></div>
              <div className="relative bg-white rounded-xl p-4 shadow-2xl">
                <img
                  src="https://static.readdy.ai/image/2733261fde4eb6ca5d5b92336d650a8a/3c043bdd7809ca918f591d4b1f076a76.jpeg"
                  alt="BEI Certified Asphalt Shingle Roof Repairability Assessor Certificate - Rocael Hernandez"
                  className="w-full h-auto rounded-lg"
                />
              </div>
            </div>

            {/* Content */}
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-14 h-14 bg-cyan-500 rounded-full flex items-center justify-center">
                  <i className="ri-award-fill text-white text-3xl"></i>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900">BEI Certified</h3>
                  <p className="text-cyan-600 font-semibold">Asphalt Shingle Roof Repairability Assessor</p>
                </div>
              </div>

              <div className="space-y-4 mb-8">
                <p className="text-gray-700 leading-relaxed">
                  Our team member <strong>Rocael Hernandez</strong> has successfully completed the rigorous <strong>Building Experts Institute</strong> certification program for Asphalt Shingle Roof Repairability Assessment.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  This certification demonstrates our commitment to providing you with the most knowledgeable and skilled professionals in the industry. We don't just fix roofs – we understand the science behind every shingle.
                </p>
              </div>

              <div className="bg-gray-50 rounded-xl p-6 mb-8">
                <h4 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <i className="ri-checkbox-circle-fill text-cyan-500"></i>
                  What This Means For You:
                </h4>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-cyan-500 mt-1"></i>
                    <span className="text-gray-700">Accurate damage assessments for insurance claims</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-cyan-500 mt-1"></i>
                    <span className="text-gray-700">Expert recommendations on repair vs. replacement</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-cyan-500 mt-1"></i>
                    <span className="text-gray-700">Industry-leading knowledge of asphalt shingle systems</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-cyan-500 mt-1"></i>
                    <span className="text-gray-700">Professional documentation that insurance companies trust</span>
                  </li>
                </ul>
              </div>

              <div className="flex items-center gap-4 text-sm text-gray-500">
                <div className="flex items-center gap-2">
                  <i className="ri-calendar-check-line text-cyan-500"></i>
                  <span>Issued: Aug 27, 2025</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="ri-shield-check-line text-cyan-500"></i>
                  <span>Valid through 2028</span>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Message */}
          <div className="mt-16 text-center bg-gradient-to-r from-cyan-500 to-cyan-600 rounded-2xl p-8">
            <h3 className="text-2xl font-bold text-white mb-3">
              Always Learning, Always Improving
            </h3>
            <p className="text-white/90 max-w-2xl mx-auto mb-6">
              At Spyder Restoration, we believe in continuous improvement. Our team regularly pursues new certifications and training to serve you with the highest level of professionalism and expertise.
            </p>
            <a
              href="#quote"
              className="inline-block px-8 py-4 bg-white text-cyan-600 text-lg font-bold rounded-full hover:bg-gray-100 transition-all duration-300 shadow-lg whitespace-nowrap cursor-pointer"
            >
              WORK WITH CERTIFIED EXPERTS <i className="ri-arrow-right-line ml-2"></i>
            </a>
          </div>
        </div>
      </section>

      {/* Service Area Map */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center mb-8">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 md:mb-0">
              PROUDLY SERVING MICHIANA
            </h2>
            <div className="flex items-center gap-2 text-cyan-600">
              <i className="ri-map-pin-fill text-2xl"></i>
              <span className="text-lg font-semibold">Elkhart, IN &amp; Surrounding Areas</span>
            </div>
          </div>

          <div className="relative rounded-2xl overflow-hidden shadow-2xl h-96 md:h-[500px]">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d95326.89474469283!2d-85.97684!3d41.68194!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x8816c4e0b7d19f8d%3A0x8c0b0b0b0b0b0b0b!2sElkhart%2C%20IN!5e0!3m2!1sen!2sus!4v1234567890"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Service Area Map"
            ></iframe>

            <div className="absolute bottom-4 left-4 bg-white/95 backdrop-blur-sm rounded-xl p-6 shadow-xl max-w-sm">
              <h3 className="font-bold text-gray-900 mb-3">SERVICE AREA INCLUDES:</h3>
              <div className="grid grid-cols-2 gap-2 text-sm text-gray-700">
                <div>• Elkhart</div>
                <div>• South Bend</div>
                <div>• Mishawaka</div>
                <div>• Goshen</div>
                <div>• Granger</div>
                <div>• Nappanee</div>
              </div>
              <a href="tel:5743503050" className="text-cyan-600 font-semibold mt-3 inline-block hover:text-cyan-700 cursor-pointer">
                Not sure? Contact us! <i className="ri-arrow-right-line"></i>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Video Showcase */}
      <section className="py-20 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-12">
            SEE OUR WORK IN ACTION
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {videosData.map((video) => (
              <div
                key={video.id}
                onClick={() => handleVideoClick(video)}
                className="relative rounded-xl overflow-hidden shadow-lg cursor-pointer group"
              >
                <img
                  src={video.thumbnail}
                  alt={video.title}
                  className="w-full h-64 object-cover object-top group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-black/40 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                  <div className="w-16 h-16 bg-cyan-500 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                    <i className="ri-play-fill text-white text-3xl ml-1"></i>
                  </div>
                </div>
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
                  <h3 className="text-white font-semibold">{video.title}</h3>
                  <span className="text-cyan-400 text-sm">{video.duration}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Reviews Section */}
      <section id="reviews" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center mb-12">
            <div>
              <p className="text-cyan-500 font-semibold mb-2 text-sm uppercase tracking-wider">CUSTOMER TESTIMONIALS</p>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">WHAT OUR CLIENTS SAY</h2>
            </div>
            <div className="flex flex-col items-end gap-3 mt-4 md:mt-0">
              <div className="flex items-center gap-2">
                <div className="flex text-amber-400">
                  {[...Array(5)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-2xl"></i>
                  ))}
                </div>
                <span className="text-2xl font-bold text-gray-900">5.0/5.0</span>
                <span className="text-gray-600">({allReviews.length}+ reviews)</span>
              </div>
              <div className="flex items-center gap-3">
                {/* Google Review Button */}
                <a
                  href="https://share.google/WYAwdrVD90oS4r8LX"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-5 py-2.5 bg-white text-gray-800 text-sm font-bold rounded-full shadow-lg hover:shadow-xl transition-all duration-300 whitespace-nowrap cursor-pointer border-2 border-transparent animate-google-pulse"
                  style={{
                    animation: 'googlePulse 2s ease-in-out infinite',
                  }}
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                  </svg>
                  <span>Leave a Google Review</span>
                  <i className="ri-external-link-line"></i>
                </a>
                <button
                  onClick={() => {
                    const reviewUrl = `${window.location.origin}${window.location.pathname}#reviews`;
                    navigator.clipboard.writeText(reviewUrl).then(() => {
                      const btn = document.getElementById('share-review-btn');
                      if (btn) {
                        const originalText = btn.innerHTML;
                        btn.innerHTML = '<i class="ri-check-line mr-2"></i>Link Copied!';
                        setTimeout(() => {
                          btn.innerHTML = originalText;
                        }, 2000);
                      }
                    });
                  }}
                  id="share-review-btn"
                  className="flex items-center gap-1 px-4 py-2 bg-cyan-500 text-white text-sm font-semibold rounded-full hover:bg-cyan-600 transition-all duration-300 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-share-line mr-2"></i>Share Review Link
                </button>
              </div>
            </div>
          </div>

          {/* Why Choose Us - Reasons */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-12">
            {[
              { icon: 'ri-shield-check-fill', text: '$0 Out of Pocket', color: 'bg-cyan-500' },
              { icon: 'ri-time-fill', text: '24/7 Emergency', color: 'bg-amber-500' },
              { icon: 'ri-award-fill', text: 'BBB A+ Rated', color: 'bg-green-500' },
              { icon: 'ri-hand-heart-fill', text: 'Lifetime Warranty', color: 'bg-rose-500' },
              { icon: 'ri-team-fill', text: 'Certified Experts', color: 'bg-indigo-500' },
              { icon: 'ri-thumb-up-fill', text: '1000+ Projects', color: 'bg-orange-500' },
            ].map((reason, idx) => (
              <div key={idx} className="bg-white rounded-xl p-4 shadow-md hover:shadow-lg transition-shadow text-center">
                <div className={`w-12 h-12 ${reason.color} rounded-full flex items-center justify-center mx-auto mb-3`}>
                  <i className={`${reason.icon} text-white text-xl`}></i>
                </div>
                <p className="text-sm font-semibold text-gray-800">{reason.text}</p>
              </div>
            ))}
          </div>

          {/* Reviews Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {allReviews.slice(0, 6).map((review) => (
              <div key={review.id} className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex text-amber-400">
                    {[...Array(review.rating)].map((_, i) => (
                      <i key={i} className="ri-star-fill"></i>
                    ))}
                  </div>
                  <span className="text-xs bg-cyan-100 text-cyan-700 px-3 py-1 rounded-full font-semibold">{review.service}</span>
                </div>
                <p className="text-gray-700 mb-4 text-sm leading-relaxed">&quot;{review.text}&quot;</p>
                <div className="flex items-center gap-3">
                  <img src={review.avatar} alt={review.name} className="w-10 h-10 rounded-full" />
                  <div>
                    <p className="font-semibold text-gray-900 text-sm">{review.name}</p>
                    <p className="text-xs text-gray-500">{review.date}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Write a Review Form */}
          <div className="bg-gradient-to-br from-cyan-500 to-cyan-600 rounded-3xl p-8 md:p-12 shadow-2xl">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              <div className="text-white">
                <h3 className="text-3xl md:text-4xl font-bold mb-4">
                  SHARE YOUR EXPERIENCE
                </h3>
                <p className="text-white/90 mb-6 text-lg">
                  Had a great experience with Spyder Restoration? We'd love to hear from you! Your feedback helps us improve and helps other homeowners make the right choice.
                </p>
                <div className="flex items-center gap-4 mb-6">
                  <div className="flex text-amber-300">
                    {[...Array(5)].map((_, i) => (
                      <i key={i} className="ri-star-fill text-3xl"></i>
                    ))}
                  </div>
                  <span className="text-xl font-bold">5-Star Reviews Only!</span>
                </div>
                <ul className="space-y-3">
                  <li className="flex items-center gap-3">
                    <i className="ri-check-double-line text-amber-300 text-xl"></i>
                    <span>Help other homeowners find quality service</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <i className="ri-check-double-line text-amber-300 text-xl"></i>
                    <span>Share your restoration success story</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <i className="ri-check-double-line text-amber-300 text-xl"></i>
                    <span>Your review may be featured on our website</span>
                  </li>
                </ul>
              </div>

              <div className="bg-white rounded-2xl p-6 md:p-8 shadow-xl">
                <form
                  id="review-form"
                  data-readdy-form
                  action="https://readdy.ai/api/form/d672ukr8scpqk7lbjm0g"
                  method="POST"
                  className="space-y-5"
                  onSubmit={(e) => {
                    e.preventDefault();
                    const form = e.currentTarget;
                    const formData = new FormData(form);
                    
                    fetch(form.action, {
                      method: 'POST',
                      headers: {
                        'Content-Type': 'application/x-www-form-urlencoded',
                      },
                      body: new URLSearchParams(formData as any).toString(),
                    })
                      .then(() => {
                        const successDiv = document.getElementById('review-success');
                        if (successDiv) {
                          successDiv.classList.remove('hidden');
                          setTimeout(() => successDiv.classList.add('hidden'), 5000);
                        }
                        form.reset();
                        
                        // Refresh reviews after submission
                        setTimeout(() => {
                          window.location.reload();
                        }, 2000);
                      })
                      .catch(() => {
                        alert('Something went wrong. Please try again.');
                      });
                  }}
                >
                  {/* Success Message */}
                  <div id="review-success" className="hidden bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-lg">
                    <div className="flex items-center gap-2">
                      <i className="ri-checkbox-circle-fill text-xl"></i>
                      <span className="font-semibold">Thank you! Your review has been submitted successfully!</span>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Your Name *</label>
                      <input
                        type="text"
                        name="reviewer_name"
                        required
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-cyan-500 focus:outline-none transition-colors text-sm"
                        placeholder="John Doe"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Email *</label>
                      <input
                        type="email"
                        name="email"
                        required
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-cyan-500 focus:outline-none transition-colors text-sm"
                        placeholder="john@example.com"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Service Received *</label>
                    <select
                      name="service_type"
                      required
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-cyan-500 focus:outline-none transition-colors text-sm"
                    >
                      <option value="">Select service</option>
                      <option value="Roofing">Roofing</option>
                      <option value="Siding">Siding</option>
                      <option value="Gutters">Gutters</option>
                      <option value="Windows">Windows</option>
                      <option value="Doors">Doors</option>
                      <option value="Storm Damage">Storm Damage</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Your Rating *</label>
                    <div className="flex items-center gap-3 text-amber-400">
                      {[...Array(5)].map((_, i) => (
                        <i key={i} className="ri-star-fill text-3xl"></i>
                      ))}
                      <span className="ml-3 text-gray-700 font-semibold">5 Stars</span>
                    </div>
                    <input type="hidden" name="rating" value="5 Stars" />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Your Review *</label>
                    <textarea
                      name="review_text"
                      rows={4}
                      required
                      maxLength={500}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-cyan-500 focus:outline-none transition-colors resize-none text-sm"
                      placeholder="Tell us about your experience with Spyder Restoration... (max 500 characters)"
                    ></textarea>
                  </div>

                  <button
                    type="submit"
                    className="w-full py-4 bg-gradient-to-r from-cyan-500 to-cyan-600 text-white text-lg font-bold rounded-full hover:from-cyan-600 hover:to-cyan-700 transition-all duration-300 shadow-lg hover:shadow-cyan-500/50 whitespace-nowrap cursor-pointer"
                  >
                    SUBMIT MY REVIEW <i className="ri-send-plane-fill ml-2"></i>
                  </button>
                </form>
              </div>
            </div>
          </div>

          {/* More Reviews Carousel */}
          <div className="mt-12">
            <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">MORE HAPPY CUSTOMERS</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {allReviews.slice(6, 14).map((review) => (
                <div key={review.id} className="bg-white rounded-xl p-5 shadow-md hover:shadow-lg transition-shadow">
                  <div className="flex text-amber-400 mb-3">
                    {[...Array(review.rating)].map((_, i) => (
                      <i key={i} className="ri-star-fill text-sm"></i>
                    ))}
                  </div>
                  <p className="text-gray-700 text-xs leading-relaxed mb-3 line-clamp-4">&quot;{review.text}&quot;</p>
                  <div className="flex items-center gap-2">
                    <img src={review.avatar} alt={review.name} className="w-8 h-8 rounded-full" />
                    <div>
                      <p className="font-semibold text-gray-900 text-xs">{review.name}</p>
                      <p className="text-xs text-cyan-600">{review.service}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Rewards Program */}
      <section className="py-20 bg-gradient-to-r from-amber-500 to-orange-500">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-white/90 font-semibold mb-2 text-sm uppercase tracking-wider">SPYDER REWARDS PROGRAM</p>
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                EARN POINTS, SAVE BIG
              </h2>
              <ul className="space-y-4 mb-8 text-white">
                <li className="flex items-center gap-3">
                  <i className="ri-check-line text-2xl"></i>
                  <span className="text-lg">Referral bonuses for every customer you send our way</span>
                </li>
                <li className="flex items-center gap-3">
                  <i className="ri-check-line text-2xl"></i>
                  <span className="text-lg">Repeat customer discounts on future projects</span>
                </li>
                <li className="flex items-center gap-3">
                  <i className="ri-check-line text-2xl"></i>
                  <span className="text-lg">Exclusive offers and early access to promotions</span>
                </li>
              </ul>
              <a
                href="#quote"
                className="inline-block px-8 py-4 bg-white text-gray-900 text-lg font-bold rounded-full hover:bg-gray-100 transition-all duration-300 shadow-lg whitespace-nowrap cursor-pointer"
              >
                JOIN NOW <i className="ri-arrow-right-line ml-2"></i>
              </a>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border-2 border-white/30">
              <div className="text-center mb-6">
                <div className="w-32 h-32 bg-white rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-5xl font-bold text-amber-500">500</span>
                </div>
                <p className="text-white text-lg">Points Available</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {['Bronze', 'Silver', 'Gold', 'Platinum'].map((tier, idx) => (
                  <div key={tier} className="bg-white/20 rounded-lg p-4 text-center">
                    <i className="ri-medal-fill text-3xl text-white mb-2"></i>
                    <p className="text-white font-semibold">{tier}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Careers Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">JOIN OUR TEAM</h2>
            <p className="text-lg text-gray-600">Building careers, building futures</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-cyan-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-money-dollar-circle-line text-cyan-600 text-3xl"></i>
              </div>
              <h3 className="font-bold text-gray-900 mb-2">Competitive Pay</h3>
              <p className="text-gray-600 text-sm">Industry-leading compensation packages</p>
            </div>
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-cyan-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-graduation-cap-line text-cyan-600 text-3xl"></i>
              </div>
              <h3 className="font-bold text-gray-900 mb-2">Training Programs</h3>
              <p className="text-gray-600 text-sm">Continuous learning and development</p>
            </div>
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-cyan-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-line-chart-line text-cyan-600 text-3xl"></i>
              </div>
              <h3 className="font-bold text-gray-900 mb-2">Growth Opportunities</h3>
              <p className="text-gray-600 text-sm">Advance your career with us</p>
            </div>
          </div>

          <div className="text-center">
            <Link
              to="/careers"
              className="inline-block px-8 py-4 bg-cyan-500 text-white text-lg font-bold rounded-full hover:bg-cyan-600 transition-all duration-300 shadow-lg whitespace-nowrap cursor-pointer"
            >
              VIEW OPEN POSITIONS <i className="ri-arrow-right-line ml-2"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-20 bg-gradient-to-br from-gray-900 via-gray-800 to-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              READY TO START YOUR PROJECT?
            </h2>
            <p className="text-xl text-cyan-400">Multiple ways to reach us</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <a
              href="tel:5743503050"
              className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 text-center hover:bg-white/20 transition-all duration-300 border border-white/20 cursor-pointer"
            >
              <div className="w-16 h-16 bg-cyan-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-phone-fill text-white text-3xl"></i>
              </div>
              <h3 className="text-white font-bold mb-2">CALL US NOW</h3>
              <p className="text-2xl text-cyan-400 font-bold mb-2">574-350-3050</p>
              <p className="text-gray-400 text-sm">Available 24/7</p>
            </a>

            <a
              href="https://wa.me/12693074842"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 text-center hover:bg-white/20 transition-all duration-300 border border-white/20 cursor-pointer"
            >
              <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-whatsapp-fill text-white text-3xl"></i>
              </div>
              <h3 className="text-white font-bold mb-2">MESSAGE US</h3>
              <p className="text-2xl text-green-400 font-bold mb-2">269-307-4842</p>
              <p className="text-gray-400 text-sm">Quick response</p>
            </a>

            <a
              href="mailto:spyderrestorationllcmi@gmail.com"
              className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 text-center hover:bg-white/20 transition-all duration-300 border border-white/20 cursor-pointer"
            >
              <div className="w-16 h-16 bg-cyan-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-mail-fill text-white text-3xl"></i>
              </div>
              <h3 className="text-white font-bold mb-2">EMAIL US</h3>
              <p className="text-lg text-cyan-400 font-bold mb-2 break-all">spyderrestorationllcmi@gmail.com</p>
              <p className="text-gray-400 text-sm">Detailed inquiries</p>
            </a>
          </div>
        </div>
      </section>

      {/* Video Modal */}
      {showVideoModal && (
        <div
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={() => setShowVideoModal(false)}
        >
          <div className="relative max-w-4xl w-full" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setShowVideoModal(false)}
              className="absolute -top-12 right-0 text-white hover:text-cyan-400 transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-4xl"></i>
            </button>
            <div className="bg-white rounded-xl p-4">
              <h3 className="text-xl font-bold text-gray-900 mb-4">{selectedVideo?.title}</h3>
              <div className="aspect-video bg-gray-900 rounded-lg flex items-center justify-center">
                <p className="text-white">Video player would be here</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
